<template>
  <button
    ref="userProfileNav"
    v-tooltip="collapsed ? 'Account menu' : ''"
    type="button"
    class="flex select-none items-center overflow-hidden border-t bg-[--v3-theme-primarycolor] p-0 text-left text-[--v3-theme-primarytextcolor] transition-[background-color,border-color,height,width] duration-150 ease-out hover:bg-[--v3-theme-primarydarkercolor] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[--color-brand-forrest-green-80]"
    :class="collapsed
      ? 'h-[34px] w-[50px] justify-center border-transparent'
      : 'h-[51px] w-[227px] border-[--v3-theme-primarydarkercolor]'"
    :aria-label="collapsed ? 'Account menu' : undefined"
    aria-controls="user-profile-panel"
    :aria-expanded="showProfilePanel"
    @click="showProfilePanel = !showProfilePanel"
  >
    <div
      class="avatar flex h-full items-center overflow-hidden transition-[margin,width] duration-150 ease-out"
      :class="collapsed ? 'mx-0 w-[34px] justify-center' : 'mx-[15px] w-[197px] justify-between'"
    >
      <div class="flex min-w-0 items-center">
        <Avatar
          :image="avatar"
          class="size-[34px] shrink-0"
          style="background-color: #ece9fc; color: #2a1261;"
          shape="circle"
        />
        <div
          class="overflow-hidden transition-[margin,max-width,opacity] duration-150 ease-out"
          :class="collapsed ? 'ml-0 max-w-0 opacity-0' : 'ml-[10px] max-w-[137px] opacity-100'"
        >
          <Skeleton v-if="isLoading" />
          <span
            v-else
            class="block truncate text-[14px] font-normal leading-[21px]"
            :class="{
              'notranslate': !!name,
            }"
            :translate="name ? 'no' : 'yes'"
          >
            {{ name ?? 'Connect your shop' }}
          </span>
        </div>
      </div>
      <div
        class="flex size-[12px] shrink-0 items-center justify-center text-[--v3-blue-gray-bluegray-400] transition-[max-width,opacity] duration-150 ease-out"
        :class="collapsed ? 'max-w-0 opacity-0' : 'max-w-[12px] opacity-100'"
      >
        <Icon :name="(showProfilePanel)? `angle-down`: `angle-up`" />
      </div>
    </div>
  </button>
  <Transition>
    <div
      v-if="showProfilePanel"
      id="user-profile-panel"
      ref="floatingProfilePanel"
      class="floatingPanel z-[110]"
      :style="floatingStyles"
    >
      <slot />
    </div>
  </Transition>
</template>

<script setup lang='ts'>
import { autoUpdate, offset, useFloating } from '@floating-ui/vue';
import Avatar from 'primevue/avatar';
import Skeleton from 'primevue/skeleton';
import { computed, ref, toRefs, watch } from 'vue';

import Icon from '@/common/components/atoms/Icon';

const props = withDefaults(defineProps<{
  avatar?: string
  collapsed?: boolean
  isLoading?: boolean
  name: string,
}>(), {
  avatar: "/img/avatar.png",
  collapsed: false,
  isLoading: false,
});
const { avatar, collapsed, isLoading, name } = toRefs(props);

const userProfileNav = ref(null);
const floatingProfilePanel = ref(null);
const showProfilePanel = ref(false);
const placement = computed(() => collapsed.value ? 'right-end' : 'top-start');
const { floatingStyles } = useFloating(userProfileNav, floatingProfilePanel, {
  placement,
  whileElementsMounted: autoUpdate,
  middleware: [offset(8)],
});

watch(collapsed, (isCollapsed) => {
  if (isCollapsed) {
    showProfilePanel.value = false;
  }
});
</script>

<style scoped>
.floatingPanel {
  transition: opacity 150ms,
              margin-top 150ms;
}

.v-enter-from,
.v-leave-to
{
  opacity: 0;
  margin-top: 15px;
}

.v-enter-to,
.v-leave-from
{
  opacity: 1;
  margin-top: 0;
}
</style>
